import { createContext, useContext, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../../../shared/services/settings/settingsService';
import type { CompanySettings, UpdateSettingsData } from '../types/settingsTypes';
import { useAuth } from '../../auth/context/AuthContext';

interface SettingsContextData {
  // Queries
  settings: CompanySettings | null;
  isLoading: boolean;
  error: string | null;
  
  // Mutations
  updateSettings: (args: { data: UpdateSettingsData, tenantTypeId: string }) => Promise<any>;
  
  // States
  isUpdating: boolean;
  
  // Actions
  setError: (error: string | null) => void;
}

interface SettingsProviderProps {
  children: ReactNode;
}

const SettingsContext = createContext<SettingsContextData | undefined>(undefined);

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Buscar configurações
  const { data: settings = null, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        const response = await settingsService.get(user?.tenants?.[0]?.typeId || '');
        console.log('Settings fetched:', response);
        return response.data;
      } catch (error: any) {
        setError(error.message || 'Erro ao buscar configurações');
        return null;
      }
    }
  });

  // Atualizar configurações
  const updateMutation = useMutation({
    mutationFn: ({ data, tenantTypeId }: { data: UpdateSettingsData, tenantTypeId: string }) => settingsService.update(data, tenantTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao atualizar configurações');
    }
  });

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoading,
        error,
        updateSettings: updateMutation.mutateAsync,
        isUpdating: updateMutation.isPending,
        setError,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
} 